// Create a Dropdown using the following markup:
//
// <div class="dropdown" data-controller="dropdown">
//   <button class="dropdown-toggle" data-action="click->dropdown#toggle click@window->dropdown#hide">Drop me down!</button>
//   <div class="dropdown-content hidden" data-target="dropdown.content" data-action="turbolinks:before-cache@window->dropdown#hide">
//     <%= link_to "Accounts", "/accounts" %>
//     <%= link_to "Home", "/" %>
//   </div>
// </div>

import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['content'];

  connect() {
    this.toggleClass = this.data.get('class') || 'hidden';
    this.toggleParentClass = this.data.get('class') || 'open';

    if (!this.hasContentTarget) return;

    this.selectedProcesses = [];
    this.selectedSnapshots = [];
    this.hiddenField = document.querySelector("#preloaded_process_templates");
    this.hiddenFieldSnapshot = document.querySelector("#snapshots");
    this.selectedContainer = document.querySelector("#selected-processes");
    this.selectedContainerSnapshot = document.querySelector("#selected-snapshots");

    const raw = this.element.dataset.processIds;
    if (raw) {
      this.selectedProcesses.push(...JSON.parse(raw));
      document.querySelectorAll(".selected-process-item").forEach((selectedItem, index) => {
        const removeBtn = selectedItem.querySelector(".remove-process");
        if (removeBtn) {
          removeBtn.addEventListener("click", this.removeProcess.bind(this));
        }
      });
    }
  }

  toggle() {
    if (!this.hasContentTarget) return;

    this.contentTarget.classList.toggle(this.toggleClass);
    this.element.classList.toggle(this.toggleParentClass);

    const dropdown = this.contentTarget;
    if (!dropdown) return;

    if (dropdown.closest(".step-meta")) {
      this.adjustDropdownPosition();
    }
  }

  hide(event) {
    if (!this.hasContentTarget) return;

    // If click occurs outside the dropdown
    if (!this.element.contains(event.target)) {
      if (!this.contentTarget.classList.contains(this.toggleClass)) {
        this.contentTarget.classList.add(this.toggleClass);
        this.element.classList.remove(this.toggleParentClass);
      }
    }

    this.adjustDropdownPosition();
  }

  resetDropdownStyles() {
    if (!this.hasContentTarget) return;

    const dropdown = this.contentTarget;
    if (!dropdown || !dropdown.style) return;

    dropdown.style.removeProperty("max-height");
    dropdown.style.removeProperty("overflow-y");
    dropdown.style.removeProperty("top");
    dropdown.style.removeProperty("bottom");
    dropdown.style.removeProperty("margin");
    dropdown.style.removeProperty("height");
  }

  adjustDropdownPosition() {
    if (!this.hasContentTarget) return;

    const dropdown = this.contentTarget;
    if (!dropdown || !dropdown.getBoundingClientRect) return;

    const rect = dropdown.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const spaceBelow = windowHeight - rect.top - 20;
    const spaceAbove = rect.top - 20;

    this.resetDropdownStyles();

    // Safe guard for scrollHeight access
    const scrollHeight = dropdown.scrollHeight || 0;

    // Downward position
    if (spaceBelow > 100) {
      dropdown.style.top = "100%";
      dropdown.style.bottom = "auto";

      if (spaceBelow < scrollHeight) {
        dropdown.style.setProperty("max-height", `${spaceBelow}px`, "important");
        dropdown.style.setProperty("overflow-y", "auto", "important");
      } else {
        dropdown.style.overflowY = "visible";
      }
    } else {
      // Upward fallback
      dropdown.style.setProperty("bottom", "0", "important");
      dropdown.style.setProperty("top", "auto", "important");
      dropdown.style.setProperty("margin", "0px 0px 40px 0px", "important");

      if (spaceAbove < scrollHeight) {
        dropdown.style.setProperty("max-height", `${spaceAbove}px`, "important");
        dropdown.style.setProperty("overflow-y", "auto", "important");
      } else {
        dropdown.style.overflowY = "visible";
      }
    }
  }

  selectProcess(event) {
    if (!this.hiddenField || !this.selectedContainer) return;

    event.preventDefault();
    const processId = event.target.dataset.id;
    const processTitle = event.target.dataset.title;

    if (!processId) return;

    if (!this.selectedProcesses.includes(processId)) {
      this.selectedProcesses.push(processId);
      this.updateHiddenField();
      this.addSelectedProcess(processId, processTitle);
    }
  }

  selectSnapshot(event) {
    if (!this.hiddenFieldSnapshot || !this.selectedContainerSnapshot) return;

    event.preventDefault();
    const snapshotId = event.target.dataset.id;
    const snapshotTitle = event.target.dataset.title;

    if (!snapshotId) return;

    if (!this.selectedSnapshots.includes(snapshotId)) {
      this.selectedSnapshots.push(snapshotId);
      this.updateHiddenFieldSnapshot();
      this.addSelectedSnapshot(snapshotId, snapshotTitle);
    }

    const itemsList = document.getElementById("items-list-snapshots");
    if (itemsList && this.selectedSnapshots.length > 0) {
      itemsList.classList.remove("hidden");
    }
  }

  addSelectedProcess(id, title) {
    if (!this.selectedContainer) return;

    const selectedItem = document.createElement("div");
    selectedItem.classList.add("selected-process-item");
    selectedItem.dataset.id = id;
    selectedItem.innerHTML = `
      ${title} <span class="remove-process icon icon-bin2" data-id="${id}"></span>
    `;
    this.selectedContainer.appendChild(selectedItem);

    const removeBtn = selectedItem.querySelector(".remove-process");
    if (removeBtn) {
      removeBtn.addEventListener("click", this.removeProcess.bind(this));
    }
  }

  addSelectedSnapshot(id, title) {
    if (!this.selectedContainerSnapshot) return;

    const selectedItem = document.createElement("div");
    selectedItem.classList.add("selected-snapshot-item");
    selectedItem.dataset.id = id;
    selectedItem.innerHTML = `
      ${title} <span class="remove-snapshot icon icon-bin2" data-id="${id}"></span>
    `;
    this.selectedContainerSnapshot.appendChild(selectedItem);

    const removeBtn = selectedItem.querySelector(".remove-snapshot");
    if (removeBtn) {
      removeBtn.addEventListener("click", this.removeSnapshot.bind(this));
    }
  }

  removeProcess(event) {
    const processId = event.target.dataset.id;
    if (!processId) return;

    this.selectedProcesses = this.selectedProcesses.filter(id => id !== processId);
    this.updateHiddenField();

    const item = event.target.closest(".selected-process-item");
    if (item) item.remove();
  }

  removeSnapshot(event) {
    const snapshotId = event.target.dataset.id;
    if (!snapshotId) return;

    this.selectedSnapshots = this.selectedSnapshots.filter(id => id !== snapshotId);
    this.updateHiddenFieldSnapshot();

    const item = event.target.closest(".selected-snapshot-item");
    if (item) item.remove();
  }

  updateHiddenField() {
    if (this.hiddenField) {
      this.hiddenField.value = JSON.stringify(this.selectedProcesses);
    }
  }

  updateHiddenFieldSnapshot() {
    if (this.hiddenFieldSnapshot) {
      this.hiddenFieldSnapshot.value = JSON.stringify(this.selectedSnapshots);
    }
  }
}
